'use client';

import { c as _c } from "react/compiler-runtime";
import { type ReactNode, useEffect, useMemo, useRef } from 'react';
import { LoadingMorph } from '@extern/ui/loading-morph';
import dynamic from 'next/dynamic';
import { usePathname, useSearchParams } from 'next/navigation';
import { useAuthEvents } from '@/app/(auth)/_hooks/use-auth-events';
import { parseLabsArrival } from '@/app/(auth)/sign-up/_utils/labs-arrival';
import { useSignUpQ1Variant } from '../_hooks/use-sign-up-q1-variant';
import { AuthLayout } from './auth-layout';
import { CenteredAuthLayout } from './centered-auth-layout';

/**
 * The Labs arrival layout is loaded only when a URL actually carries a lab result, so the packs it
 * renders from never reach the bundle of an ordinary sign-up or sign-in load.
 */
const LabsArrivalLayout = dynamic(() => import('./labs-arrival/labs-arrival-layout').then(mod => mod.LabsArrivalLayout), {
  loading: () => <AuthLoading />,
  ssr: false
});
function AuthLoading() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex min-h-screen w-full flex-col items-center justify-center bg-role-surface"><LoadingMorph /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
const CONTROL_LAYOUT_TEXT = <>
    Gain Real Experience.
    <br />
    Build Essential Skills.
    <br />
    Drive Your Career Forward.
  </>;
interface AuthLayoutSwitcherProps {
  children: ReactNode;
}
export function AuthLayoutSwitcher(t0) {
  const $ = _c(17);
  const {
    children
  } = t0;
  const {
    isReady,
    variant
  } = useSignUpQ1Variant();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const {
    trackAuthScreenViewed
  } = useAuthEvents();
  const lastTrackedPathRef = useRef(null);
  let t1;
  if ($[0] !== pathname || $[1] !== searchParams) {
    t1 = pathname?.includes("/sign-up") ? parseLabsArrival(searchParams) : null;
    $[0] = pathname;
    $[1] = searchParams;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const labsArrival = t1;
  let t2;
  let t3;
  if ($[3] !== isReady || $[4] !== labsArrival || $[5] !== pathname || $[6] !== trackAuthScreenViewed) {
    t2 = () => {
      if (!(isReady && pathname)) {
        return;
      }
      if (lastTrackedPathRef.current === pathname) {
        return;
      }
      if (pathname.includes("/sign-up")) {
        trackAuthScreenViewed("sign_up", "entry", "start", labsArrival ? {
          labs_rail: true,
          labs_archetype: labsArrival.archetypeId
        } : undefined);
        lastTrackedPathRef.current = pathname;
        return;
      }
      if (pathname.includes("/sign-in")) {
        trackAuthScreenViewed("sign_in", "entry", "start");
        lastTrackedPathRef.current = pathname;
      }
    };
    t3 = [isReady, labsArrival, pathname, trackAuthScreenViewed];
    $[3] = isReady;
    $[4] = labsArrival;
    $[5] = pathname;
    $[6] = trackAuthScreenViewed;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  useEffect(t2, t3);
  if (!isReady) {
    let t4;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <AuthLoading />;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    return t4;
  }
  let t4;
  if ($[10] !== children || $[11] !== variant) {
    t4 = variant === "experiment" ? <CenteredAuthLayout>{children}</CenteredAuthLayout> : <AuthLayout text={CONTROL_LAYOUT_TEXT}>{children}</AuthLayout>;
    $[10] = children;
    $[11] = variant;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const shipped = t4;
  if (labsArrival) {
    let t5;
    if ($[13] !== children || $[14] !== labsArrival || $[15] !== shipped) {
      t5 = <LabsArrivalLayout fallback={shipped} params={labsArrival}>{children}</LabsArrivalLayout>;
      $[13] = children;
      $[14] = labsArrival;
      $[15] = shipped;
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    return t5;
  }
  return shipped;
}